<template>
  <div class="goal-suggestion-box-container">
    <div class="goal-suggestion-header">
      <h4>Goal Suggestion</h4>
    </div>
    <div class="goal-suggestion-container">
      <div class="goal-suggestion-text-box">
        <p v-if="!isLoading" data-test="goal-suggestion-text">
          {{ text }}
        </p>
        <div v-else>
          <div v-if="!promptRequestInProgress">Click 'Run' to generate a suggestion...</div>
          <LoadingSpinner v-else :type="'Dots'" size="100px" data-test="goal-suggestion-loading" />
        </div>
      </div>
    </div>
    <div class="prompt-buttons-container">
      <div v-show="suggestionHistoryLength > 0" class="buttons-centre">
        <button
          class="btn btn-default-fab-chevron"
          type="button"
          @click="goToPrevSuggestion"
          aria-label="Previous suggestion"
          data-test="previous-suggestion-button"
        />
        <span data-test="suggestion-count"
          >{{ activeSuggestionIndex + 1 }} / {{ suggestionHistoryLength }}</span
        >
        <button
          class="btn btn-default-fab-chevron"
          type="button"
          @click="goToNextSuggestion"
          aria-label="Next suggestion"
          data-test="next-suggestion-button"
        />
      </div>
      <div class="buttons-end">
        <button
          v-if="suggestionHistoryLength > 0"
          class="btn btn-sm btn-default-tick"
          type="button"
          @click="useSuggestion"
          data-test="use-suggestion-button"
        >
          Use
        </button>
        <button
          class="btn btn-sm btn-default-reload"
          type="button"
          @click="reRunPrompt"
          data-test="regenerate-suggestion-button"
        >
          {{ suggestionHistoryLength > 0 ? "Regenerate" : "Run" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { AxiosError } from "axios";
import LoadingSpinner from "../loading-spinner.vue";
import processPromptVars, { getDataContext } from "../smart-scratchpad/process-prompt-vars";
import { renderErrorMessageToClient } from "../smart-scratchpad/variable-error-helpers";
import chat from "../smart-scratchpad/chat";
import goalCaptureStore from "../../stores/goal-capture-store";
import { Analytics } from "../../services/analytics/analytics";
import { csContext } from "../../services/cs-context";

const props = defineProps<{ goalInput: string; prompt: string; goalIndex: number }>();

const text = ref<string>("");
const promptRequestInProgress = ref(false);
const lastPromptIsFinished = ref(true);
const regenerateCount = ref(0);

const goalKey = `goal-${props.goalIndex}`;
const isLoading = computed(() => text.value.length === 0 || promptRequestInProgress.value);
const goalInput = computed(() => props.goalInput);
const goalInputVersions = computed(() => goalCaptureStore.version);
const previousSuggestions = computed(
  () => goalCaptureStore.goalSuggestions[goalKey]?.suggestions ?? "",
);

const emit = defineEmits(["update:goalSuggestion", "update:goalName"]);

let version = 0;

let dataContext: any = null;

const previousOutputsPromptText = computed(() => {
  return (
    "\n\nWhen I've recently asked you the prompt that you're about to see, these are the outputs you have produced:" +
    `\n\n['${previousSuggestions.value.join("', '")}']` +
    "\n\nMake sure the next output you produce does not replicate one of the above outputs."
  );
});

const promptWithPrefixing = computed(() => {
  return (
    `\n\nThis number is for tracking, you can ignore this number: ${regenerateCount.value}` +
    `${previousSuggestions.value.length > 0 ? previousOutputsPromptText.value : ""}` +
    "\n\nHere's the prompt that you should generate your response from:" +
    `\n${props.prompt}`
  );
});

function useSuggestion() {
  emit("update:goalName", text.value);
}

function goToPrevSuggestion() {
  text.value = goalCaptureStore.goToPreviousSuggestion(props.goalIndex);
}

function goToNextSuggestion() {
  text.value = goalCaptureStore.goToNextSuggestion(props.goalIndex);
}

async function askPrompt(): Promise<void> {
  const activeVersion = version;
  promptRequestInProgress.value = true;
  lastPromptIsFinished.value = false;

  if (promptWithPrefixing.value?.trim().length > 0) {
    dataContext.reader.active_text = goalInput.value;
    const { processed, errors } = await processPromptVars(promptWithPrefixing.value, dataContext);

    if (errors.length > 0) {
      text.value = renderErrorMessageToClient(errors);
      return;
    }

    Analytics.action("Ran Team Goal Suggestion", "GoalSuggestionActions", {
      teamLeadId: csContext.user.id,
      teamId: csContext.currentTeam?.id,
    });

    chat(processed, (content) => {
      if (activeVersion === version) {
        promptRequestInProgress.value = false;
        text.value = content.trim();
      }
    })
      .then((message) => {
        goalCaptureStore.addGoalSuggestion(text.value, props.goalIndex);
        emit("update:goalSuggestion", text.value);

        Analytics.action("Completed Team Goal Suggestion", "GoalSuggestionActions", {
          teamLeadId: csContext.user.id,
          teamId: csContext.currentTeam?.id,
        });
      })
      .catch((e) => {
        text.value = "Sorry, there was an error. Please refresh the page.";
        const error = e as AxiosError;
        const errorMessage = `${error.response?.status}: ${error.response?.statusText}`;

        Analytics.action("Failed Team Goal Suggestion", "GoalSuggestionActions", {
          error: errorMessage,
          teamId: csContext.currentTeam?.id,
          teamLeadId: csContext.user.id,
        });
      })
      .finally(() => {
        lastPromptIsFinished.value = true;
      });
  }
}

const reRunPrompt = async () => {
  if (lastPromptIsFinished.value) {
    regenerateCount.value += 1;
    await askPrompt();
  }
};

const suggestionHistoryLength = ref(0);
const activeSuggestionIndex = ref(0);

watchEffect(() => {
  if (goalCaptureStore.goalSuggestions[goalKey]) {
    activeSuggestionIndex.value =
      goalCaptureStore.goalSuggestions[goalKey].activeSuggestionIndex ?? 0;
    suggestionHistoryLength.value = goalCaptureStore.goalSuggestions[goalKey].suggestions.length;
    text.value = goalCaptureStore.goalSuggestions[goalKey].suggestions[activeSuggestionIndex.value];
  }
});

watch(goalInputVersions, async () => {
  version += 1;

  if (text.value !== goalInput.value && lastPromptIsFinished.value) {
    await askPrompt();
  }
});

onMounted(async () => {
  dataContext = getDataContext();
});
</script>
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.goal-suggestion-box-container {
  margin-bottom: 40px;
  background-color: @off-white;
  border-radius: 5px;
}

.goal-suggestion-header {
  padding: 10px 20px;
}

.goal-suggestion-container {
  display: flex;
  align-items: center;

  img {
    margin-right: 10px;
    width: 30px;
  }
}

.goal-suggestion-text-box {
  padding: 6px 24px 20px;
  resize: none;
  font-size: 16px;
  position: relative;
  width: 100%;

  p {
    margin: 0;
  }
}

.prompt-buttons-container {
  padding: 0 20px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;

  @media (max-width: 650px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
}

.buttons-centre {
  grid-column: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: @gray-light;

  button {
    width: 30px;
    height: 30px;
    transform: scale(0.9);
  }

  button:first-of-type {
    transform: scale(0.9) rotate(180deg);
  }
}

.buttons-end {
  grid-column: 3;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  button {
    transform: scale(0.9);
  }
}
</style>
